import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Search, X } from 'lucide-react';
import { PROPERTY_SECTORS, LOAN_STATUSES } from '@/constants/loan-filters';
import { useRef, useEffect } from 'react';

interface LoansSearchFiltersProps {
  searchTerm: string;
  sectorFilter: string;
  statusFilter: string;
  onSearchChange: (value: string) => void;
  onSectorChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onClearFilters: () => void;
}

export const LoansSearchFilters = ({
  searchTerm,
  sectorFilter,
  statusFilter,
  onSearchChange,
  onSectorChange,
  onStatusChange,
  onClearFilters,
}: LoansSearchFiltersProps) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const wasInputFocusedRef = useRef(false);

  const hasActiveFilters =
    searchTerm || sectorFilter !== 'all' || statusFilter !== 'all';

  // Track if input was focused and restore focus after filter changes
  useEffect(() => {
    const input = searchInputRef.current;
    if (!input) return;

    const checkFocus = () => {
      wasInputFocusedRef.current = document.activeElement === input;
    };

    const handleFocus = () => {
      wasInputFocusedRef.current = true;
    };

    const handleBlur = () => {
      // Delay check to see if focus moved to a filter dropdown
      setTimeout(() => {
        const activeElement = document.activeElement;
        // Keep focus if user is interacting with filters or input still has value
        if (
          activeElement !== input &&
          !activeElement?.closest('[role="combobox"]') &&
          !searchTerm
        ) {
          wasInputFocusedRef.current = false;
        }
      }, 100);
    };

    input.addEventListener('focus', handleFocus);
    input.addEventListener('blur', handleBlur);
    // Also check on mousedown in case user clicks in input
    input.addEventListener('mousedown', checkFocus);

    return () => {
      input.removeEventListener('focus', handleFocus);
      input.removeEventListener('blur', handleBlur);
      input.removeEventListener('mousedown', checkFocus);
    };
  }, [searchTerm]);

  // Restore focus after filter changes if it was previously focused
  useEffect(() => {
    if (wasInputFocusedRef.current && searchInputRef.current) {
      // Use requestAnimationFrame to restore focus after render
      const timeoutId = setTimeout(() => {
        const input = searchInputRef.current;
        if (input && document.activeElement !== input) {
          input.focus();
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [sectorFilter, statusFilter]);

  return (
    <div
      className="flex flex-col md:flex-row gap-4 mb-8"
      data-test-id="search-filters-section"
    >
      <div className="flex-1 relative" data-test-id="search-container">
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4"
          data-test-id="search-icon"
        />
        <Input
          ref={searchInputRef}
          type="text"
          placeholder="Search by ID, Client Loan Number, Loan Name, or Borrower..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10"
          data-test-id="search-input"
        />
      </div>
      <Select
        value={sectorFilter}
        onValueChange={onSectorChange}
        data-test-id="sector-filter"
      >
        <SelectTrigger
          className="w-full md:w-[200px]"
          data-test-id="sector-filter-trigger"
        >
          <SelectValue
            placeholder="All Sectors"
            data-test-id="sector-filter-value"
          />
        </SelectTrigger>
        <SelectContent data-test-id="sector-filter-content">
          <SelectItem value="all" data-test-id="sector-filter-all">
            All Sectors
          </SelectItem>
          {PROPERTY_SECTORS.map((sector, index) => (
            <SelectItem
              key={sector}
              value={sector}
              data-test-id={`sector-filter-${index}`}
            >
              {sector}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={statusFilter}
        onValueChange={onStatusChange}
        data-test-id="status-filter"
      >
        <SelectTrigger
          className="w-full md:w-[200px]"
          data-test-id="status-filter-trigger"
        >
          <SelectValue
            placeholder="All Statuses"
            data-test-id="status-filter-value"
          />
        </SelectTrigger>
        <SelectContent data-test-id="status-filter-content">
          <SelectItem value="all" data-test-id="status-filter-all">
            All Statuses
          </SelectItem>
          {LOAN_STATUSES.map((status, index) => (
            <SelectItem
              key={status}
              value={status}
              data-test-id={`status-filter-${index}`}
            >
              {status}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasActiveFilters && (
        <Button
          variant="outline"
          onClick={onClearFilters}
          className="flex items-center gap-2"
          data-test-id="clear-filters-button"
        >
          <X className="h-4 w-4" data-test-id="clear-filters-icon" />
          Clear
        </Button>
      )}
    </div>
  );
};
